import React, { useEffect, useState } from 'react';
import axios from 'axios';
import toast from 'react-hot-toast';
import DashLayout from '../../components/DashLayout';

const nav = [
  { to: '/super-admin', icon: '📊', label: 'Dashboard' },
  { to: '/super-admin/businesses', icon: '🏢', label: 'Businesses' },
  { to: '/super-admin/payments', icon: '💰', label: 'Payments' },
  { to: '/super-admin/invoice-report', icon: '🧾', label: 'Invoice Report' },
  { to: '/super-admin/tickets', icon: '🎫', label: 'Support Tickets' },

  { to: '/super-admin/plans', icon: '📦', label: 'Plans' },
  { to: '/super-admin/agents', icon: '👥', label: 'Agent Requests' }
];

const STATUS_COLORS = {
  active: { bg: 'rgba(0,213,91,0.1)', text: '#00d55b' },
  suspended: { bg: 'rgba(255,68,68,0.1)', text: '#ff4444' },
  pending_payment: { bg: 'rgba(245,158,11,0.1)', text: '#f59e0b' },
  pending_approval: { bg: 'rgba(245,158,11,0.1)', text: '#f59e0b' },
  deleted: { bg: '#1a1a1a', text: '#555' },
  blocked: { bg: 'rgba(255,68,68,0.1)', text: '#ff4444' }
};

export default function Businesses() {
  const [businesses, setBusinesses] = useState([]);
  const [filter, setFilter] = useState('all');
  const [search, setSearch] = useState('');
  const [editModal, setEditModal] = useState(null);
  const [deleteModal, setDeleteModal] = useState(null);
  const [extendModal, setExtendModal] = useState(null);
  const [extendDays, setExtendDays] = useState(30);
  const [loading, setLoading] = useState(false);

  const load = () => {
    setLoading(true);
    axios.get('/api/super-admin/businesses?limit=200')
      .then(r => setBusinesses(r.data.data || []))
      .catch(() => toast.error('Failed to load businesses'))
      .finally(() => setLoading(false));
  };

  useEffect(() => { load(); }, []);

  const updateStatus = async (id, status) => {
    try {
      await axios.put(`/api/super-admin/businesses/${id}/status`, { status });
      toast.success(`Status updated to ${status}`);
      load();
    } catch { toast.error('Update failed'); }
  };

  const saveEdit = async () => {
    try {
      await axios.put(`/api/super-admin/businesses/${editModal.id}/edit`, editModal);
      toast.success('Business updated successfully');
      setEditModal(null);
      load();
    } catch { toast.error('Update failed'); }
  };

  const deleteBusiness = async () => {
    try {
      await axios.delete(`/api/super-admin/businesses/${deleteModal.id}`);
      toast.success('Business deleted permanently');
      setDeleteModal(null);
      load();
    } catch { toast.error('Delete failed'); }
  };

  const extendPlan = async () => {
    try {
      await axios.put(`/api/super-admin/businesses/${extendModal.id}/extend`, { days: extendDays });
      toast.success(`Plan extended by ${extendDays} days`);
      setExtendModal(null);
      load();
    } catch { toast.error('Extension failed'); }
  };

  const resetPassword = async (business) => {
    const newPass = window.prompt(`Set new password for ${business.company_name}:`);
    if (!newPass || newPass.length < 8) return toast.error('Password must be at least 8 characters');
    try {
      await axios.put(`/api/super-admin/businesses/${business.id}/reset-password`, { new_password: newPass });
      toast.success('Password reset successfully');
    } catch { toast.error('Reset failed'); }
  };

  const filtered = businesses.filter(b => {
    const matchFilter = filter === 'all' || b.account_status === filter;
    const matchSearch = !search || b.company_name?.toLowerCase().includes(search.toLowerCase()) || b.email?.toLowerCase().includes(search.toLowerCase()) || b.phone?.includes(search);
    return matchFilter && matchSearch;
  });

  const counts = {};
  businesses.forEach(b => { counts[b.account_status] = (counts[b.account_status] || 0) + 1; });

  return (
    <DashLayout navItems={nav} role="Super Admin">
      <div style={{ padding: 24 }}>
        <div style={{ marginBottom: 24 }}>
          <h1 style={{ fontSize: 22, fontWeight: 700 }}>Client Management</h1>
          <p style={{ color: '#555', fontSize: 13, marginTop: 4 }}>{businesses.length} total businesses</p>
        </div>

        {/* Edit Modal */}
        {editModal && (
          <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.9)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 100, padding: 20 }}>
            <div style={{ background: '#111', border: '1px solid #1e1e1e', borderRadius: 16, padding: 28, width: '100%', maxWidth: 520, maxHeight: '90vh', overflowY: 'auto' }}>
              <h3 style={{ fontSize: 18, fontWeight: 700, marginBottom: 20, color: '#00d55b' }}>Edit: {editModal.company_name}</h3>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 14 }}>
                {[
                  ['Company Name', 'company_name'],
                  ['Owner Name', 'owner_name'],
                  ['Phone', 'phone']
                ].map(([label, key]) => (
                  <div key={key}>
                    <label style={{ fontSize: 12, color: '#888', display: 'block', marginBottom: 5 }}>{label}</label>
                    <input value={editModal[key] || ''} onChange={e => setEditModal(m => ({ ...m, [key]: e.target.value }))} />
                  </div>
                ))}
                <div>
                  <label style={{ fontSize: 12, color: '#888', display: 'block', marginBottom: 5 }}>Plan</label>
                  <select value={editModal.plan_key || ''} onChange={e => setEditModal(m => ({ ...m, plan_key: e.target.value }))}>
                    <option value="pack_one">Pack One (₹5,200/$99)</option>
                    <option value="pack_two">Pack Two (₹6,799/$149)</option>
                    <option value="pack_advanced">Pack Advanced (₹10,299/$299)</option>
                  </select>
                </div>
                <div>
                  <label style={{ fontSize: 12, color: '#888', display: 'block', marginBottom: 5 }}>Status</label>
                  <select value={editModal.account_status || ''} onChange={e => setEditModal(m => ({ ...m, account_status: e.target.value }))}>
                    <option value="active">Active</option>
                    <option value="suspended">Suspended</option>
                    <option value="pending_payment">Pending Payment</option>
                    <option value="pending_approval">Pending Approval</option>
                    <option value="blocked">Blocked</option>
                  </select>
                </div>
                <div>
                  <label style={{ fontSize: 12, color: '#888', display: 'block', marginBottom: 5 }}>Billing Cycle</label>
                  <select value={editModal.billing_cycle || 'monthly'} onChange={e => setEditModal(m => ({ ...m, billing_cycle: e.target.value }))}>
                    <option value="monthly">Monthly</option>
                    <option value="quarterly">Quarterly</option>
                    <option value="yearly">Yearly</option>
                  </select>
                </div>
                <div>
                  <label style={{ fontSize: 12, color: '#888', display: 'block', marginBottom: 5 }}>Currency</label>
                  <select value={editModal.currency || 'inr'} onChange={e => setEditModal(m => ({ ...m, currency: e.target.value }))}>
                    <option value="inr">INR</option>
                    <option value="usd">USD</option>
                  </select>
                </div>
                <div style={{ gridColumn: '1 / -1' }}>
                  <label style={{ fontSize: 12, color: '#888', display: 'block', marginBottom: 5 }}>Plan Expires At</label>
                  <input type="datetime-local" value={editModal.plan_expires_at ? editModal.plan_expires_at.substring(0, 16) : ''} onChange={e => setEditModal(m => ({ ...m, plan_expires_at: e.target.value }))} />
                </div>
              </div>
              <div style={{ display: 'flex', gap: 10, marginTop: 20 }}>
                <button onClick={() => setEditModal(null)} style={{ flex: 1, padding: '11px', background: '#1a1a1a', border: '1px solid #1e1e1e', color: '#fff', borderRadius: 8, cursor: 'pointer', fontSize: 14 }}>Cancel</button>
                <button onClick={saveEdit} style={{ flex: 2, padding: '11px', background: '#00d55b', color: '#000', borderRadius: 8, fontWeight: 700, cursor: 'pointer', fontSize: 14 }}>Save Changes</button>
              </div>
            </div>
          </div>
        )}

        {/* Delete Modal */}
        {deleteModal && (
          <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.9)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 100 }}>
            <div style={{ background: '#111', border: '1px solid #ff4444', borderRadius: 16, padding: 28, width: '100%', maxWidth: 420 }}>
              <div style={{ fontSize: 40, textAlign: 'center', marginBottom: 16 }}>⚠️</div>
              <h3 style={{ fontSize: 18, fontWeight: 700, marginBottom: 8, color: '#ff4444', textAlign: 'center' }}>Permanently Delete</h3>
              <p style={{ color: '#888', fontSize: 14, textAlign: 'center', marginBottom: 6 }}>
                You are about to permanently delete <strong style={{ color: '#fff' }}>{deleteModal.company_name}</strong>
              </p>
              <p style={{ color: '#555', fontSize: 12, textAlign: 'center', marginBottom: 24 }}>
                This will delete all chats, agents, messages and data. This action cannot be undone.
              </p>
              <div style={{ display: 'flex', gap: 10 }}>
                <button onClick={() => setDeleteModal(null)} style={{ flex: 1, padding: '11px', background: '#1a1a1a', border: '1px solid #1e1e1e', color: '#fff', borderRadius: 8, cursor: 'pointer', fontSize: 14 }}>Cancel</button>
                <button onClick={deleteBusiness} style={{ flex: 2, padding: '11px', background: '#ff4444', color: '#fff', borderRadius: 8, fontWeight: 700, cursor: 'pointer', fontSize: 14 }}>Yes, Delete Permanently</button>
              </div>
            </div>
          </div>
        )}

        {/* Extend Plan Modal */}
        {extendModal && (
          <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.9)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 100 }}>
            <div style={{ background: '#111', border: '1px solid #1e1e1e', borderRadius: 16, padding: 28, width: '100%', maxWidth: 380 }}>
              <h3 style={{ fontSize: 18, fontWeight: 700, marginBottom: 8 }}>Extend Plan</h3>
              <p style={{ color: '#555', fontSize: 13, marginBottom: 20 }}>Extend plan for <strong style={{ color: '#00d55b' }}>{extendModal.company_name}</strong></p>
              <div style={{ marginBottom: 16 }}>
                <label style={{ fontSize: 12, color: '#888', display: 'block', marginBottom: 6 }}>Number of days to extend</label>
                <input type="number" value={extendDays} onChange={e => setExtendDays(Number(e.target.value))} min={1} max={365} />
              </div>
              <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', marginBottom: 16 }}>
                {[30, 60, 90, 365].map(d => (
                  <button key={d} onClick={() => setExtendDays(d)} style={{ padding: '6px 14px', borderRadius: 6, border: `1px solid ${extendDays === d ? '#00d55b' : '#1e1e1e'}`, background: extendDays === d ? 'rgba(0,213,91,0.1)' : '#1a1a1a', color: extendDays === d ? '#00d55b' : '#888', fontSize: 12, cursor: 'pointer' }}>
                    {d} days
                  </button>
                ))}
              </div>
              <div style={{ display: 'flex', gap: 10 }}>
                <button onClick={() => setExtendModal(null)} style={{ flex: 1, padding: '10px', background: '#1a1a1a', border: '1px solid #1e1e1e', color: '#fff', borderRadius: 8, cursor: 'pointer' }}>Cancel</button>
                <button onClick={extendPlan} style={{ flex: 2, padding: '10px', background: '#00d55b', color: '#000', borderRadius: 8, fontWeight: 700, cursor: 'pointer' }}>Extend Plan</button>
              </div>
            </div>
          </div>
        )}

        {/* Filters */}
        <div style={{ display: 'flex', gap: 12, marginBottom: 20, flexWrap: 'wrap' }}>
          <input value={search} onChange={e => setSearch(e.target.value)} placeholder="Search by name, email or phone..." style={{ flex: 1, minWidth: 220 }} />
          <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
            {[['all', 'All'], ['active', 'Active'], ['pending_approval', 'Pending'], ['suspended', 'Suspended'], ['blocked', 'Blocked']].map(([s, l]) => (
              <button key={s} onClick={() => setFilter(s)} style={{ padding: '7px 14px', borderRadius: 8, border: `1px solid ${filter === s ? '#00d55b' : '#1e1e1e'}`, background: filter === s ? 'rgba(0,213,91,0.1)' : 'transparent', color: filter === s ? '#00d55b' : '#555', fontSize: 12, cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 5 }}>
                {l}
                {s !== 'all' && counts[s] > 0 && <span style={{ background: filter === s ? '#00d55b' : '#1e1e1e', color: filter === s ? '#000' : '#888', fontSize: 10, padding: '0 5px', borderRadius: 8, fontWeight: 700 }}>{counts[s]}</span>}
              </button>
            ))}
          </div>
        </div>

        {/* Table */}
        <div style={{ background: '#111', border: '1px solid #1e1e1e', borderRadius: 12, overflow: 'hidden' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse' }}>
            <thead>
              <tr style={{ borderBottom: '1px solid #1e1e1e', background: '#0d0d0d' }}>
                {['Business', 'Contact', 'Plan', 'Status', 'Expires', 'Actions'].map(h => (
                  <th key={h} style={{ padding: '11px 14px', textAlign: 'left', fontSize: 11, color: '#555', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.05em' }}>{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr><td colSpan={6} style={{ padding: 40, textAlign: 'center', color: '#555', fontSize: 13 }}>Loading...</td></tr>
              ) : filtered.length === 0 ? (
                <tr><td colSpan={6} style={{ padding: 40, textAlign: 'center', color: '#333', fontSize: 13 }}>No businesses found</td></tr>
              ) : filtered.map(b => {
                const expiryDate = b.plan_expires_at ? new Date(b.plan_expires_at) : null;
                const daysLeft = expiryDate ? Math.ceil((expiryDate - new Date()) / (1000 * 60 * 60 * 24)) : null;
                const expiringSoon = daysLeft !== null && daysLeft <= 7 && daysLeft > 0;
                const expired = daysLeft !== null && daysLeft <= 0;
                return (
                  <tr key={b.id} style={{ borderBottom: '1px solid #0d0d0d' }}>
                    <td style={{ padding: '12px 14px' }}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                        <div style={{ width: 34, height: 34, borderRadius: '50%', background: '#1a1a1a', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 13, fontWeight: 700, color: '#00d55b', flexShrink: 0 }}>
                          {b.company_name?.[0]?.toUpperCase() || '?'}
                        </div>
                        <div>
                          <div style={{ fontSize: 13, fontWeight: 500 }}>{b.company_name}</div>
                          <div style={{ fontSize: 11, color: '#555' }}>{b.owner_name}</div>
                        </div>
                      </div>
                    </td>
                    <td style={{ padding: '12px 14px' }}>
                      <div style={{ fontSize: 12, color: '#888' }}>{b.email}</div>
                      <div style={{ fontSize: 11, color: '#555' }}>{b.phone}</div>
                    </td>
                    <td style={{ padding: '12px 14px' }}>
                      <div style={{ fontSize: 12, color: '#00d55b', fontWeight: 500 }}>{b.plan_name || b.plan_key?.replace(/_/g, ' ')}</div>
                      <div style={{ fontSize: 11, color: '#555' }}>{b.billing_cycle === 'yearly' ? 'Yearly' : b.billing_cycle === 'quarterly' ? 'Quarterly' : 'Monthly'} · {b.currency?.toUpperCase()}</div>
                    </td>
                    <td style={{ padding: '12px 14px' }}>
                      <span style={{ fontSize: 11, padding: '3px 10px', borderRadius: 20, background: STATUS_COLORS[b.account_status]?.bg || '#1a1a1a', color: STATUS_COLORS[b.account_status]?.text || '#888' }}>
                        {b.account_status?.replace(/_/g, ' ')}
                      </span>
                    </td>
                    <td style={{ padding: '12px 14px' }}>
                      {expiryDate ? (
                        <>
                          <div style={{ fontSize: 12, color: expired ? '#ff4444' : expiringSoon ? '#f59e0b' : '#888' }}>
                            {expiryDate.toLocaleDateString()}
                          </div>
                          <div style={{ fontSize: 11, color: expired ? '#ff4444' : expiringSoon ? '#f59e0b' : '#555' }}>
                            {expired ? 'Expired' : expiringSoon ? `${daysLeft}d left ⚠️` : `${daysLeft}d left`}
                          </div>
                        </>
                      ) : <span style={{ fontSize: 12, color: '#555' }}>—</span>}
                    </td>
                    <td style={{ padding: '12px 14px' }}>
                      <div style={{ display: 'flex', gap: 5, flexWrap: 'wrap' }}>
                        {/* Edit */}
                        <button onClick={() => setEditModal({ ...b })}
                          style={{ padding: '5px 10px', background: '#1a1a1a', border: '1px solid #1e1e1e', color: '#888', borderRadius: 6, fontSize: 11, cursor: 'pointer' }}
                          title="Edit business">
                          ✏️ Edit
                        </button>

                        {/* Activate */}
                        {b.account_status !== 'active' && (
                          <button onClick={() => updateStatus(b.id, 'active')}
                            style={{ padding: '5px 10px', background: 'rgba(0,213,91,0.1)', border: '1px solid #00d55b', color: '#00d55b', borderRadius: 6, fontSize: 11, cursor: 'pointer' }}
                            title="Activate account">
                            ✓ Activate
                          </button>
                        )}

                        {/* Suspend */}
                        {b.account_status === 'active' && (
                          <button onClick={() => updateStatus(b.id, 'suspended')}
                            style={{ padding: '5px 10px', background: 'rgba(245,158,11,0.1)', border: '1px solid #f59e0b', color: '#f59e0b', borderRadius: 6, fontSize: 11, cursor: 'pointer' }}
                            title="Suspend account">
                            ⏸ Suspend
                          </button>
                        )}

                        {/* Block */}
                        {b.account_status !== 'blocked' ? (
                          <button onClick={() => updateStatus(b.id, 'blocked')}
                            style={{ padding: '5px 10px', background: 'rgba(255,68,68,0.08)', border: '1px solid #ff4444', color: '#ff4444', borderRadius: 6, fontSize: 11, cursor: 'pointer' }}
                            title="Block account">
                            🚫 Block
                          </button>
                        ) : (
                          <button onClick={() => updateStatus(b.id, 'active')}
                            style={{ padding: '5px 10px', background: 'rgba(0,213,91,0.08)', border: '1px solid #00d55b', color: '#00d55b', borderRadius: 6, fontSize: 11, cursor: 'pointer' }}
                            title="Unblock account">
                            ✓ Unblock
                          </button>
                        )}

                        {/* Extend Plan */}
                        <button onClick={() => setExtendModal(b)}
                          style={{ padding: '5px 10px', background: '#1a1a1a', border: '1px solid #1e1e1e', color: '#888', borderRadius: 6, fontSize: 11, cursor: 'pointer' }}
                          title="Extend plan">
                          📅 Extend
                        </button>

                        {/* Reset Password */}
                        <button onClick={() => resetPassword(b)}
                          style={{ padding: '5px 10px', background: '#1a1a1a', border: '1px solid #1e1e1e', color: '#888', borderRadius: 6, fontSize: 11, cursor: 'pointer' }}
                          title="Reset password">
                          🔑 Password
                        </button>

                        {/* Delete */}
                        <button onClick={() => setDeleteModal(b)}
                          style={{ padding: '5px 10px', background: '#1a0000', border: '1px solid #ff4444', color: '#ff4444', borderRadius: 6, fontSize: 11, cursor: 'pointer' }}
                          title="Delete permanently">
                          🗑 Delete
                        </button>
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>

        <div style={{ marginTop: 12, fontSize: 12, color: '#333', textAlign: 'right' }}>
          Showing {filtered.length} of {businesses.length} businesses
        </div>
      </div>
    </DashLayout>
  );
}